import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { MapPinLine, CircleNotch, WarningCircle, CheckCircle } from '@phosphor-icons/react';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT') : '—');

const AdminUncoveredZonesPage = () => {
  const [zones, setZones] = useState([]);
  const [meta, setMeta] = useState({ days: 30, centers_comuni: 0 });
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchZones = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/admin/uncovered-zones?days=${days}`);
      setZones(r.data?.zones || []);
      setMeta({ days: r.data?.days || days, centers_comuni: r.data?.centers_comuni || 0 });
    } catch { setZones([]); } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetchZones(); }, [fetchZones]);

  const totRecent = zones.reduce((a, z) => a + z.requests_recent, 0);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8" data-testid="admin-uncovered-zones-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-2xl text-foreground">Zone scoperte dai Centri</h2>
          <p className="text-sm text-foreground/60 mt-1">
            Comuni dove non opera alcun Centro di Assistenza: le richieste di queste zone vanno
            direttamente ai professionisti.
          </p>
        </div>
        <div className="flex gap-2">
          {[30, 90, 365].map((d) => (
            <Button
              key={d} size="sm" variant={days === d ? 'default' : 'outline'}
              className="rounded-full h-8" onClick={() => setDays(d)}
              data-testid={`zones-range-${d}`}
            >
              {d === 365 ? '12 mesi' : `${d} giorni`}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <Card className="rounded-2xl p-4" data-testid="zones-kpi-count">
          <p className="text-xs text-foreground/60">Comuni scoperti</p>
          <p className="font-heading font-bold text-2xl">{zones.length}</p>
        </Card>
        <Card className="rounded-2xl p-4" data-testid="zones-kpi-requests">
          <p className="text-xs text-foreground/60">Richieste negli ultimi {meta.days} giorni</p>
          <p className="font-heading font-bold text-2xl">{totRecent}</p>
        </Card>
        <Card className="rounded-2xl p-4" data-testid="zones-kpi-centers">
          <p className="text-xs text-foreground/60">Comuni presidiati da Centri</p>
          <p className="font-heading font-bold text-2xl">{meta.centers_comuni}</p>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><CircleNotch size={28} className="animate-spin text-primary" /></div>
      ) : zones.length === 0 ? (
        <Card className="rounded-2xl p-10 text-center" data-testid="zones-empty">
          <CheckCircle size={44} weight="duotone" className="text-primary mx-auto mb-3" />
          <p className="text-foreground/70">Nessuna zona scoperta: tutti i comuni con richieste hanno un Centro.</p>
        </Card>
      ) : (
        <Card className="rounded-2xl overflow-hidden" data-testid="zones-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-foreground/60">
              <tr>
                <th className="text-left font-medium px-4 py-3">Comune</th>
                <th className="text-left font-medium px-4 py-3">Provincia / Regione</th>
                <th className="text-right font-medium px-4 py-3">Richieste {meta.days}gg</th>
                <th className="text-right font-medium px-4 py-3">Totali</th>
                <th className="text-left font-medium px-4 py-3">Ultima richiesta</th>
                <th className="text-left font-medium px-4 py-3">CAP coinvolti</th>
                <th className="text-right font-medium px-4 py-3">Professionisti</th>
                <th className="text-left font-medium px-4 py-3">Avviso admin</th>
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => (
                <tr key={z.comune} className="border-t border-border/70" data-testid={`zone-row-${z.comune}`}>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 font-medium">
                      <MapPinLine size={15} weight="duotone" className="text-primary" />
                      {z.comune}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground/70">{z.provincia || '—'} · {z.regione || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {z.requests_recent > 0
                      ? <Badge className="bg-amber-100 text-amber-900 border border-amber-300">{z.requests_recent}</Badge>
                      : <span className="text-foreground/40">0</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-foreground/70">{z.requests_total}</td>
                  <td className="px-4 py-3 text-foreground/70">{fmtDate(z.last_request_at)}</td>
                  <td className="px-4 py-3 text-foreground/70">
                    {z.caps.length ? z.caps.slice(0, 5).join(', ') : `${z.caps_comune} CAP nel comune`}
                    {z.caps.length > 5 ? '…' : ''}
                  </td>
                  <td className="px-4 py-3 text-right text-foreground/70">{z.professionals}</td>
                  <td className="px-4 py-3">
                    {z.alerted_at
                      ? <span className="text-xs text-foreground/60">Inviato il {fmtDate(z.alerted_at)}</span>
                      : <span className="inline-flex items-center gap-1 text-xs text-foreground/50">
                          <WarningCircle size={14} /> Mai inviato
                        </span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

export default AdminUncoveredZonesPage;
